import { Atproto } from "../lib/atproto.ts";
import type { Author, Post, ThreadEvent } from "../lib/model.ts";
import { BrowserNetwork, type Network } from "../lib/network.ts";
import { missingParents } from "../lib/tree.ts";

export class Thread {
  root = "";
  selected = "";
  posts = new Map<string, Post>();
  quotes = new Map<string, Post>();
  authors = new Map<string, Author>();
  phase: "backlinks" | "parents" | "done" = "backlinks";
  #cursor: string | null = null;
  #cursors = new Set<string>();
  #discovered = false;
  #pending = new Set<string>();

  constructor(
    readonly input: string,
    private network: Network = new BrowserNetwork(),
  ) {}

  async load(
    signal: AbortSignal,
    update: (status: string) => void,
  ): Promise<void> {
    if (this.phase === "done") return;
    const abort = new AbortController();
    const combined = AbortSignal.any([signal, abort.signal]);
    const client = new Atproto(this.network, combined);
    const emit = (event: ThreadEvent) => {
      combined.throwIfAborted();
      switch (event.type) {
        case "thread":
          if (
            this.root &&
            (event.root !== this.root || event.selected !== this.selected)
          ) {
            throw new Error(
              "The submitted post changed threads while loading. Reload to start again.",
            );
          }
          this.root = event.root;
          this.selected = event.selected;
          break;
        case "post":
          if (event.post.record || !this.posts.get(event.post.uri)?.record) {
            this.posts.set(event.post.uri, event.post);
          }
          break;
        case "quote":
          this.quotes.set(event.post.uri, event.post);
          break;
        case "author":
          if (event.author.pds || !this.authors.has(event.author.did)) {
            this.authors.set(event.author.did, event.author);
          }
          break;
      }
      update(
        this.phase === "backlinks" ? "Loading replies…" : "Checking parents…",
      );
    };
    let work = Promise.withResolvers<void>();
    const wake = () => work.resolve();
    combined.addEventListener("abort", wake, { once: true });
    try {
      await client.thread(this.selected || this.input, emit);
      this.#pending.add(this.root);
      this.#pending.add(this.selected);
      await Promise.all([
        (async () => {
          while (!this.#discovered) {
            combined.throwIfAborted();
            const page = await client.backlinks(this.root, this.#cursor);
            combined.throwIfAborted();
            if (page.cursor !== null && this.#cursors.has(page.cursor)) {
              throw new Error(
                "Backlinks returned a repeated cursor. Reload to start again.",
              );
            }
            for (const uri of page.uris) {
              if (!this.posts.has(uri)) this.#pending.add(uri);
            }
            // Keep discovered URIs when advancing the cursor, even if hydration is paused.
            this.#cursor = page.cursor;
            this.#discovered = page.cursor === null;
            if (page.cursor !== null) this.#cursors.add(page.cursor);
            wake();
          }
        })(),
        (async () => {
          while (this.phase !== "done") {
            combined.throwIfAborted();
            if (!this.#pending.size) {
              if (!this.#discovered) {
                work = Promise.withResolvers<void>();
                await work.promise;
                continue;
              }
              this.phase = "parents";
              for (const uri of missingParents(this.root, this.posts)) {
                this.#pending.add(uri);
              }
              if (!this.#pending.size) {
                this.phase = "done";
                break;
              }
            }
            const uris = [...this.#pending].slice(0, 50);
            await client.batch(uris, emit);
            combined.throwIfAborted();
            for (const uri of uris) this.#pending.delete(uri);
          }
        })(),
      ]);
    } finally {
      abort.abort();
      combined.removeEventListener("abort", wake);
    }
  }
}
